import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  type Project,
  getProjects,
  deleteProject,
  getProjectProgress,
  formatCurrency,
  isProjectOverdue,
} from '../../features/projects';
import { Card, DashboardPageHeader, DashboardStatCard, DeleteConfirmModal } from '../../shared/ui';
import { getUserFriendlyErrorMessage, logErrorInDev } from '../../shared/lib';
import ProjectModal from '../../components/ProjectModal';
import ProjectMilestonesSection from '../../components/dashboard/ProjectMilestonesSection';

const ProjectsPage: React.FC = () => {
  const { user, profile } = useAuth();
  const { showToast } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed' | 'overdue'>('all');
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);

  const canView = !!profile && can(profile, 'projects', 'view');
  const canEdit = !!profile && can(profile, 'projects', 'edit');
  const canDelete = !!profile && can(profile, 'projects', 'delete');

  useEffect(() => {
    if (canView) {
      getProjects()
        .then(setProjects)
        .catch((err) => {
          logErrorInDev('Failed to load projects', err);
          showToast(getUserFriendlyErrorMessage(err, 'Failed to load projects.'), 'error');
        });
    }
  }, [canView, showToast]);

  const handleSaved = () => {
    showToast('Project saved successfully.');
    getProjects()
      .then(setProjects)
      .catch((err) => {
        logErrorInDev('Failed to refresh projects', err);
        showToast(getUserFriendlyErrorMessage(err, 'Failed to refresh projects.'), 'error');
      });
    setShowModal(false);
    setEditingProject(null);
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setShowModal(true);
  };

  const handleDeleteClick = (project: Project) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;
    setIsDeleting(true);
    try {
      await deleteProject(projectToDelete.id);
      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete.id));
      setShowDeleteModal(false);
      setProjectToDelete(null);
      showToast('Project deleted.');
    } catch (err) {
      logErrorInDev('Failed to delete project', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to delete project.'), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const stats = useMemo(() => {
    const totalFundsRaised = projects.reduce((sum, p) => sum + (p.fundsRaised || 0), 0);
    const activeProjects = projects.filter((p) => !p.isCompleted).length;
    const criticalDeadlines = projects.filter((p) => !p.isCompleted && isProjectOverdue(p.dtEndDate)).length;
    return { totalFundsRaised, activeProjects, criticalDeadlines };
  }, [projects]);

  const filteredProjects = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return projects.filter((p) => {
      const isOverdue = isProjectOverdue(p.dtEndDate);
      const matchesSearch =
        !term ||
        p.name.toLowerCase().includes(term) ||
        (p.description ?? '').toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && !p.isCompleted && !isOverdue) ||
        (statusFilter === 'completed' && p.isCompleted) ||
        (statusFilter === 'overdue' && !p.isCompleted && isOverdue);
      return matchesSearch && matchesStatus;
    });
  }, [projects, searchTerm, statusFilter]);

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-shield-exclamation me-2"></i>
            <strong>Access Restricted:</strong> You do not have permission to view projects.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <DashboardPageHeader
          title="Projects Management"
          description="Track funding progress, milestones, and deadlines in one place."
          icon="bi bi-folder"
          actions={
            canEdit ? (
              <button className="btn btn-primary btn-sm" onClick={() => { setEditingProject(null); setShowModal(true); }}>
                <i className="bi bi-plus-circle me-1"></i>Add New Project
              </button>
            ) : null
          }
        >
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
              <span className="input-group-text bg-light">
                <i className="bi bi-search text-muted" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search projects…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="input-group input-group-sm" style={{ maxWidth: 220 }}>
              <span className="input-group-text bg-light">
                <i className="bi bi-funnel text-muted" />
              </span>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              >
                <option value="all">All projects</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>
        </DashboardPageHeader>

        {/* Stats Cards */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <DashboardStatCard
              variant="panel-emphasis"
              title="Total Funds Raised"
              value={formatCurrency(stats.totalFundsRaised)}
              icon="bi bi-wallet2"
            />
          </div>
          <div className="col-md-4">
            <DashboardStatCard
              variant="panel"
              tone="success"
              title="Active Projects"
              value={stats.activeProjects}
              icon="bi bi-check-circle"
            />
          </div>
          <div className="col-md-4">
            <DashboardStatCard
              variant="panel"
              tone="danger"
              title="Critical Deadlines"
              value={String(stats.criticalDeadlines).padStart(2, '0')}
              icon="bi bi-calendar-x"
            />
          </div>
        </div>

        <Card title="Projects" icon="bi bi-folder">
          {projects.length === 0 ? (
            <div className="text-center py-4">
              <i className="bi bi-folder-x fs-1 text-muted mb-3"></i>
              <p className="text-muted">No projects found. Create your first project to get started.</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-4">
              <i className="bi bi-search fs-1 text-muted mb-3"></i>
              <p className="text-muted">No projects match your search or filter.</p>
            </div>
          ) : (
            <div className="row g-4">
              {filteredProjects.map((project) => {
                const progress = getProjectProgress(project.fundsRaised, project.fundsNeeded);
                const isOverdue = isProjectOverdue(project.dtEndDate);
                const isExpanded = expandedProjectId === project.id;
                return (
                  <div key={project.id} className="col-12">
                    <div className={`project-card ${project.isCompleted ? 'project-completed' : ''}`}>
                      {/* Main Clickable Area */}
                      <div
                        className="project-card-main"
                        role="button"
                        tabIndex={0}
                        onClick={() => setExpandedProjectId((id) => (id === project.id ? null : project.id))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpandedProjectId((id) => (id === project.id ? null : project.id));
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse milestones' : 'Expand to show milestones'}
                      >
                        {/* Header Row - Title, Category, Date */}
                        <div className="project-card-header">
                          <div className="project-card-title-section">
                            <h5 className={`project-title ${project.isCompleted ? 'text-muted' : ''}`}>
                              {project.name}
                            </h5>
                            {project.category && (
                              <span className="project-category-badge">{project.category}</span>
                            )}
                          </div>
                          <div className="project-card-date">
                            <i className="bi bi-calendar3" />
                            <span>{new Date(project.dtStartDate).toLocaleDateString('en-ZA', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '/')}</span>
                            <span className="date-separator">-</span>
                            <span>{new Date(project.dtEndDate).toLocaleDateString('en-ZA', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '/')}</span>
                          </div>
                        </div>

                        {/* Status Badges */}
                        <div className="project-status-badges">
                          {!project.isCompleted && !isOverdue && (
                            <span className="status-badge status-badge--active">ACTIVE</span>
                          )}
                          {project.isPublic && (
                            <span className="status-badge status-badge--public">PUBLIC</span>
                          )}
                          {project.isCompleted && (
                            <span className="status-badge status-badge--completed">COMPLETED</span>
                          )}
                          {isOverdue && !project.isCompleted && (
                            <span className="status-badge status-badge--overdue">OVERDUE</span>
                          )}
                        </div>

                        {/* Progress Section */}
                        {!project.isCompleted && (
                          <div className="project-progress-section">
                            <div className="project-progress-header">
                              <div className="project-funding-text">
                                <span className="funding-label">Rands Raised:</span>
                                <span className="funding-value funding-value--raised">{formatCurrency(project.fundsRaised)}</span>
                                <span className="funding-separator">/</span>
                                <span className="funding-label">Rands Needed:</span>
                                <span className="funding-value funding-value--needed">{formatCurrency(project.fundsNeeded)}</span>
                              </div>
                              <div className="project-progress-percentage">{progress}%</div>
                            </div>
                            <div className="project-progress-bar-container">
                              <div className="project-progress-track">
                                <div
                                  className="project-progress-fill"
                                  style={{ width: `${progress}%` }}
                                />
                                <div
                                  className="project-progress-indicator"
                                  style={{ left: `${progress}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {project.isCompleted && (
                          <div className="project-completed-banner">
                            <i className="bi bi-check-circle-fill" />
                            <span>Project Completed — Total Raised: {formatCurrency(project.fundsRaised)}</span>
                          </div>
                        )}
                      </div>

                      {/* Footer Row */}
                      <div className="project-card-footer" onClick={(e) => e.stopPropagation()}>
                        <div
                          className="project-milestones-toggle"
                          role="button"
                          tabIndex={0}
                          onClick={() => setExpandedProjectId((id) => (id === project.id ? null : project.id))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setExpandedProjectId((id) => (id === project.id ? null : project.id));
                            }
                          }}
                        >
                          <span>Click to show milestones</span>
                          <i className={`bi bi-chevron-down ${isExpanded ? 'rotated' : ''}`} />
                        </div>
                        <div className="project-card-meta">
                          <div className="project-meta-dates">
                            <span>CREATED: {new Date(project.dtCreated).toLocaleDateString('en-ZA', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '/')}</span>
                            <span>UPDATED: {(() => {
                              const updated = new Date(project.dtUpdated);
                              const now = new Date();
                              const diffMs = now.getTime() - updated.getTime();
                              const diffMins = Math.floor(diffMs / 60000);
                              const diffHours = Math.floor(diffMs / 3600000);
                              const diffDays = Math.floor(diffMs / 86400000);
                              if (diffMins < 1) return 'just now';
                              if (diffMins < 60) return `${diffMins} MINUTE${diffMins > 1 ? 'S' : ''} AGO`;
                              if (diffHours < 24) return `${diffHours} HOUR${diffHours > 1 ? 'S' : ''} AGO`;
                              if (diffDays < 7) return `${diffDays} DAY${diffDays > 1 ? 'S' : ''} AGO`;
                              return updated.toLocaleDateString('en-ZA', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '/');
                            })()}</span>
                          </div>
                          <div className="project-card-actions">
                            {canEdit && (
                              <button className="project-action-btn" onClick={() => handleEdit(project)} aria-label="Edit">
                                <i className="bi bi-pencil" />
                              </button>
                            )}
                            {canDelete && (
                              <button className="project-action-btn project-action-btn--delete" onClick={() => handleDeleteClick(project)} aria-label="Delete">
                                <i className="bi bi-trash" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Milestones */}
                      {isExpanded && (
                        <div className="project-milestones-expanded">
                          <ProjectMilestonesSection projectId={project.id} allowMutations={canEdit} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {showModal && (
        <ProjectModal
          show={showModal}
          onHide={() => { setShowModal(false); setEditingProject(null); }}
          onProjectSaved={handleSaved}
          project={editingProject}
          userId={user?.id ?? ''}
        />
      )}
      {showDeleteModal && projectToDelete && (
        <DeleteConfirmModal
          show={showDeleteModal}
          onHide={cancelDelete}
          onConfirm={confirmDelete}
          itemName={projectToDelete.name}
          itemType="project"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default ProjectsPage;
